import {MarkdownAtom, markdownService, MarkdownViewer, MultilineEditor} from '@dv5/components'
import {clearDescriptionEditRequest, getDocumentDescriptionEditRequest, getDocumentDescriptions, useAppDispatch, useAppSelector} from '@dv5/domain'
import React, {FC, MouseEvent, useEffect, useRef} from 'react'
import {documentApi} from '../services/documentService'
import {useDocumentDescription} from '../panels/useDocumentDescription'
import {PartSaveStatus} from './PartSaveStatus'
import './DocumentDescription.less'

interface Props {
    documentId: string
}

export const DocumentDescription: FC<Props> = ({documentId}) => {
    const dispatch = useAppDispatch()
    const descriptions = useAppSelector(getDocumentDescriptions)
    const description = descriptions[documentId] || ''
    const descriptionEditRequest = useAppSelector(getDocumentDescriptionEditRequest)
    const editRequest = descriptionEditRequest[documentId] ?? 0
    const lastHandledRequestRef = useRef(editRequest)

    const {
        editMode,
        content,
        setContent,
        saveStatus,
        lastSavedTime,
        toggleEditMode,
        enterEditMode,
        cancelEdit,
        handleManualSave,
    } = useDocumentDescription({documentId, description})

    // Consume an edit request coming from the document header menu.
    useEffect(() => {
        if (editRequest !== lastHandledRequestRef.current) {
            lastHandledRequestRef.current = editRequest
            enterEditMode()
            dispatch(clearDescriptionEditRequest({documentId}))
        }
    }, [editRequest, documentId, enterEditMode, dispatch])

    const toggleCheckBox = (atom: MarkdownAtom) => {
        const newText = markdownService.getAfterToggleCheckbox(description, atom)
        documentApi.changeDescription(documentId, newText)
    }

    const handleViewClick = (event: MouseEvent<HTMLDivElement>) => {
        let target = event.target as HTMLElement | null
        while (target !== null) {
            const tag = target.tagName.toLowerCase()
            if (tag === 'a' || tag === 'input' || tag === 'button') {
                return
            }
            target = target.parentElement
        }
        toggleEditMode()
    }

    const hasDescription = description.trim() !== ''

    if (editMode) {
        return (
            <div className='documentDescription documentDescription--editing'>
                <MultilineEditor value={content}
                                 setValue={setContent}
                                 onConfirm={handleManualSave}
                                 onCancel={cancelEdit}
                                 settings={{
                                     autoResize: true,
                                     lineNumbers: true,
                                     autoFocus: true,
                                 }}
                                 disabled={false}
                />
                <PartSaveStatus saveStatus={saveStatus} lastSavedTime={lastSavedTime}/>
            </div>
        )
    }

    if (!hasDescription) {
        return null
    }

    return (
        <div className='documentDescription'
             onClick={handleViewClick}
             title='Click to edit description'
        >
            <MarkdownViewer sourceCode={description}
                            compactMode={false}
                            onToggleCheckBox={toggleCheckBox}
            />
        </div>
    )
}
